import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { 
  Users, Phone, MapPin, Clock, CheckCircle, XCircle, 
  RefreshCw, Search, Filter, Eye, ArrowRight, Calendar,
  Wifi, TrendingUp, User
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_TYPES = [
  { value: 'internet', label: 'İnternet' },
  { value: 'iptv', label: 'IPTV' },
  { value: 'catv', label: 'CATV' },
  { value: 'telefon', label: 'Telefon' }
];

const STATUS_CONFIG = {
  new: { label: 'Yeni', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', tabColor: 'bg-yellow-500', tabHover: 'hover:bg-yellow-600' },
  contacted: { label: 'Əlaqə alınıb', color: 'bg-blue-100 text-blue-800 border-blue-300', tabColor: 'bg-blue-500', tabHover: 'hover:bg-blue-600' },
  converted: { label: 'Çevrildi', color: 'bg-green-100 text-green-800 border-green-300', tabColor: 'bg-green-500', tabHover: 'hover:bg-green-600' },
  cancelled: { label: 'Ləğv', color: 'bg-red-100 text-red-800 border-red-300', tabColor: 'bg-red-500', tabHover: 'hover:bg-red-600' }
};

const STATUS_TABS = [
  { id: 'all', label: 'Hamısı', color: 'bg-zinc-500', hoverColor: 'hover:bg-zinc-600' },
  { id: 'new', label: 'Yeni', color: 'bg-yellow-500', hoverColor: 'hover:bg-yellow-600' },
  { id: 'contacted', label: 'Əlaqə alınıb', color: 'bg-blue-500', hoverColor: 'hover:bg-blue-600' },
  { id: 'converted', label: 'Çevrildi', color: 'bg-green-500', hoverColor: 'hover:bg-green-600' },
  { id: 'cancelled', label: 'Ləğv', color: 'bg-red-500', hoverColor: 'hover:bg-red-600' },
];

export default function D2DPanel({ user }) {
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [filterService, setFilterService] = useState('');
  
  // Convert dialog state
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [convertRecord, setConvertRecord] = useState(null);
  const [convertGroupId, setConvertGroupId] = useState('');
  const [groups, setGroups] = useState([]);
  
  // Cancel dialog state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelRecord, setCancelRecord] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  
  // Detail dialog state
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchRecords = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (activeTab && activeTab !== 'all') params.append('status', activeTab);
      if (filterService && filterService !== 'all') params.append('service_type', filterService);
      
      const res = await axios.get(`${API}/d2d/records?${params.toString()}`, { headers });
      setRecords(res.data.records || []);
    } catch (error) {
      console.error('D2D records fetch error:', error);
    }
  }, [activeTab, filterService]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/d2d/stats`, { headers });
      setStats(res.data);
    } catch (error) {
      console.error('D2D stats fetch error:', error);
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/groups`, { headers });
      setGroups(res.data || []);
    } catch (error) {
      console.error('Groups fetch error:', error);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchRecords(), fetchStats(), fetchGroups()]).finally(() => setLoading(false));
  }, [fetchRecords, fetchStats, fetchGroups]);

  useEffect(() => {
    fetchRecords();
  }, [activeTab, filterService, fetchRecords]);

  // Filter records by search term
  const filteredRecords = records.filter(record => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      record.full_name?.toLowerCase().includes(search) ||
      record.phone?.includes(search) ||
      record.address?.toLowerCase().includes(search) ||
      record.agent_name?.toLowerCase().includes(search)
    );
  });

  // Update status
  const handleUpdateStatus = async (recordId, newStatus) => {
    try {
      await axios.put(`${API}/d2d/records/${recordId}/status`, 
        { status: newStatus },
        { headers }
      );
      toast.success('Status yeniləndi');
      fetchRecords();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Status yeniləmək mümkün olmadı');
    }
  };

  // Convert to connection
  const handleConvert = async () => {
    if (!convertRecord) return;
    
    if (!convertGroupId) {
      toast.error('Qrup seçilməlidir');
      return;
    }
    
    try {
      const res = await axios.post(`${API}/d2d/records/${convertRecord.id}/convert`, 
        { group_id: convertGroupId }, 
        { headers }
      );
      toast.success('D2D qeydi qoşulmaya çevrildi');
      setShowConvertDialog(false);
      setConvertRecord(null);
      setConvertGroupId('');
      fetchRecords();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Qoşulmaya çevirmək mümkün olmadı');
    }
  };

  // Cancel record
  const handleCancel = async () => {
    if (!cancelRecord) return;
    
    try {
      await axios.put(`${API}/d2d/records/${cancelRecord.id}/status`, 
        { status: 'cancelled', cancel_reason: cancelReason },
        { headers }
      );
      toast.success('D2D qeydi ləğv edildi');
      setShowCancelDialog(false);
      setCancelRecord(null);
      setCancelReason('');
      fetchRecords();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ləğv etmək mümkün olmadı');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('az-AZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getServiceLabel = (record) => {
    // Check for service_types array first (new format)
    if (record.service_types && Array.isArray(record.service_types)) {
      return record.service_types.map(v => SERVICE_TYPES.find(s => s.value === v)?.label || v).join(' + ');
    }
    // Fallback to service_type string (old format)
    const value = record.service_type || record;
    if (typeof value === 'string') {
      // Handle "internet+iptv" format
      if (value.includes('+')) {
        return value.split('+').map(v => SERVICE_TYPES.find(s => s.value === v.trim())?.label || v).join(' + ');
      }
      return SERVICE_TYPES.find(s => s.value === value)?.label || value;
    }
    return '-';
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status Tabs */}
      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map(tab => {
          const count = tab.id === 'all' 
            ? stats?.total || 0 
            : stats?.[tab.id] || 0;
          const isActive = activeTab === tab.id;
          
          return (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              variant={isActive ? 'default' : 'outline'}
              className={`${isActive ? `${tab.color} ${tab.hoverColor} text-white` : 'hover:bg-zinc-100'}`}
              data-testid={`d2d-tab-${tab.id}`}
            >
              {tab.label} ({count})
            </Button>
          );
        })}
        
        {/* Conversion Rate Badge */}
        {stats && (
          <div className="flex items-center gap-2 ml-auto px-3 py-2 bg-purple-100 text-purple-700 rounded-lg">
            <TrendingUp className="h-4 w-4" />
            <span className="font-semibold">Çevrilmə: {stats.conversion_rate}%</span>
          </div>
        )}
      </div>

      {/* Search and Filter Row */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            placeholder="Axtarış (ad, telefon, ünvan, agent)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="d2d-search"
          />
        </div>
        
        <Select value={filterService} onValueChange={setFilterService}>
          <SelectTrigger className="w-full md:w-48" data-testid="d2d-filter-service">
            <SelectValue placeholder="Xidmət növü" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Bütün Xidmətlər</SelectItem>
            {SERVICE_TYPES.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Records Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            D2D Qeydləri ({filteredRecords.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="bg-zinc-50 border-b">
                <tr>
                  <th className="text-left p-3 text-sm font-medium text-zinc-600">Tarix</th>
                  <th className="text-left p-3 text-sm font-medium text-zinc-600 hidden md:table-cell">Agent</th>
                  <th className="text-left p-3 text-sm font-medium text-zinc-600">Soyad Ad</th>
                  <th className="text-left p-3 text-sm font-medium text-zinc-600">Telefon</th>
                  <th className="text-left p-3 text-sm font-medium text-zinc-600 hidden lg:table-cell">Ünvan</th>
                  <th className="text-left p-3 text-sm font-medium text-zinc-600 hidden sm:table-cell">Xidmət</th>
                  <th className="text-left p-3 text-sm font-medium text-zinc-600">Status</th>
                  <th className="text-left p-3 text-sm font-medium text-zinc-600">Əməliyyatlar</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-8 text-zinc-500">
                      D2D qeydi tapılmadı
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map((record) => (
                    <tr key={record.id} className="border-b hover:bg-zinc-50">
                      <td className="p-3 text-sm">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-zinc-400" />
                          {formatDate(record.created_at)}
                        </div>
                      </td>
                      <td className="p-3 text-sm hidden md:table-cell">
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3 text-zinc-400" />
                          {record.agent_name || '-'}
                        </div>
                      </td>
                      <td className="p-3 font-medium">{record.full_name}</td>
                      <td className="p-3">
                        <a href={`tel:${record.phone}`} className="text-blue-600 hover:underline flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {record.phone}
                        </a>
                      </td>
                      <td className="p-3 text-sm max-w-[200px] truncate hidden lg:table-cell" title={record.address}>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-zinc-400 flex-shrink-0" />
                          {record.address}
                        </div>
                      </td>
                      <td className="p-3 hidden sm:table-cell">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-100 rounded text-sm">
                          <Wifi className="h-3 w-3" />
                          {getServiceLabel(record)}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium border ${STATUS_CONFIG[record.status]?.color || 'bg-zinc-100'}`}>
                          {STATUS_CONFIG[record.status]?.label || record.status}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setDetailRecord(record);
                              setShowDetailDialog(true);
                            }}
                            title="Ətraflı bax"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          
                          {(record.status === 'new' || record.status === 'contacted') && (
                            <>
                              {record.status === 'new' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-blue-600 hover:text-blue-700"
                                  onClick={() => handleUpdateStatus(record.id, 'contacted')}
                                  title="Əlaqə alınıb"
                                >
                                  <Phone className="h-4 w-4" />
                                </Button>
                              )}
                              
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-green-600 hover:text-green-700"
                                onClick={() => {
                                  setConvertRecord(record);
                                  setShowConvertDialog(true);
                                }}
                                title="Qoşulmaya çevir"
                              >
                                <ArrowRight className="h-4 w-4" />
                              </Button>
                              
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => {
                                  setCancelRecord(record);
                                  setShowCancelDialog(true);
                                }}
                                title="Ləğv et"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Top Agents */}
      {stats?.top_agents && stats.top_agents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Ən Yaxşı D2D Agentlər
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-zinc-50 border-b">
                <tr>
                  <th className="text-left p-3 text-sm font-medium text-zinc-600">#</th>
                  <th className="text-left p-3 text-sm font-medium text-zinc-600">Agent</th>
                  <th className="text-center p-3 text-sm font-medium text-zinc-600">Qeydlər</th>
                  <th className="text-center p-3 text-sm font-medium text-zinc-600">Çevrildi</th>
                  <th className="text-center p-3 text-sm font-medium text-zinc-600">Çevrilmə %</th>
                </tr>
              </thead>
              <tbody>
                {stats.top_agents.map((agent, index) => (
                  <tr key={agent.agent_id} className="border-b">
                    <td className="p-3 font-bold text-zinc-500">{index + 1}</td>
                    <td className="p-3 font-medium">{agent.agent_name || '-'}</td>
                    <td className="p-3 text-center">{agent.total_records}</td>
                    <td className="p-3 text-center text-green-600 font-medium">{agent.converted}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        agent.conversion_rate >= 50 ? 'bg-green-100 text-green-700' :
                        agent.conversion_rate >= 25 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {agent.conversion_rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Convert Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={(open) => {
        setShowConvertDialog(open);
        if (!open) {
          setConvertGroupId('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Qoşulmaya Çevir</DialogTitle>
            <DialogDescription>
              Bu D2D qeydini yeni qoşulmaya çevirmək istəyirsiniz?
            </DialogDescription>
          </DialogHeader>
          
          {convertRecord && (
            <div className="space-y-3 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-zinc-500">Soyad Ad</p>
                  <p className="font-medium">{convertRecord.full_name}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Telefon</p>
                  <p className="font-medium">{convertRecord.phone}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-zinc-500">Ünvan</p>
                  <p className="font-medium">{convertRecord.address}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Xidmət</p>
                  <p className="font-medium">{getServiceLabel(convertRecord)}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Agent</p>
                  <p className="font-medium">{convertRecord.agent_name}</p>
                </div>
              </div>
              
              {/* Group Selection */}
              <div className="pt-4 border-t">
                <Label className="text-sm font-medium">
                  Qrup/Region seçin <span className="text-red-500">*</span>
                </Label>
                <Select value={convertGroupId} onValueChange={setConvertGroupId}>
                  <SelectTrigger className="mt-2" data-testid="convert-group-select">
                    <SelectValue placeholder="Qrup seçin..." />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map(group => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowConvertDialog(false);
              setConvertGroupId('');
            }}>
              Ləğv et
            </Button>
            <Button 
              onClick={handleConvert} 
              className="bg-green-600 hover:bg-green-700"
              disabled={!convertGroupId}
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              Qoşulmaya Çevir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>D2D Qeydini Ləğv Et</DialogTitle>
            <DialogDescription>
              Bu qeydi ləğv etmək istəyirsiniz? Səbəbi qeyd edin.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Ləğv səbəbi</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Müştəri imtina etdi, əlaqə yoxdur, və s."
              className="mt-2"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCancelDialog(false);
              setCancelReason('');
            }}>
              Geri
            </Button>
            <Button 
              onClick={handleCancel} 
              className="bg-red-600 hover:bg-red-700"
              disabled={!cancelReason.trim()}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Ləğv Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>D2D Qeydi Detalları</DialogTitle>
          </DialogHeader>
          
          {detailRecord && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-zinc-500">Soyad Ad</p>
                  <p className="font-medium">{detailRecord.full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Telefon</p>
                  <a href={`tel:${detailRecord.phone}`} className="font-medium text-blue-600">
                    {detailRecord.phone}
                  </a>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-zinc-500">Ünvan</p>
                  <p className="font-medium">{detailRecord.address}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Xidmət növü</p>
                  <p className="font-medium">{getServiceLabel(detailRecord)}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Status</p>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-medium border ${STATUS_CONFIG[detailRecord.status]?.color}`}>
                    {STATUS_CONFIG[detailRecord.status]?.label}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Agent</p>
                  <p className="font-medium">{detailRecord.agent_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Tarix</p>
                  <p className="font-medium">{formatDate(detailRecord.created_at)}</p>
                </div>
                
                {detailRecord.location && (
                  <div className="col-span-2">
                    <p className="text-sm text-zinc-500">GPS Lokasiya</p>
                    <a 
                      href={`https://www.google.com/maps?q=${detailRecord.location.lat},${detailRecord.location.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <MapPin className="h-4 w-4" />
                      Xəritədə göstər
                    </a>
                  </div>
                )}
                
                {detailRecord.note && (
                  <div className="col-span-2">
                    <p className="text-sm text-zinc-500">Qeyd</p>
                    <p className="font-medium bg-zinc-50 p-2 rounded">{detailRecord.note}</p>
                  </div>
                )}
                
                {detailRecord.photo_url && (
                  <div className="col-span-2">
                    <p className="text-sm text-zinc-500 mb-2">Şəkil</p>
                    <img 
                      src={detailRecord.photo_url} 
                      alt="D2D şəkil" 
                      className="max-w-full rounded border"
                    />
                  </div>
                )}
                
                {detailRecord.cancel_reason && (
                  <div className="col-span-2">
                    <p className="text-sm text-zinc-500">Ləğv səbəbi</p>
                    <p className="font-medium text-red-600 bg-red-50 p-2 rounded">
                      {detailRecord.cancel_reason}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Bağla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
